import type { Result } from '../core/result';
import { ok, err } from '../core/result';
import type { Layout } from './types';
import type {
  WaterBody,
  WaterBodyKind,
  WaterClass,
  WaterClassesFile,
  WaterVotes,
} from './generated/waterClasses';
import { createAjvFactory } from './validate';
import schema from './schema/waterClasses.schema.json';

export type { WaterBody, WaterBodyKind, WaterClass, WaterClassesFile, WaterVotes };

export const WATER_CLASSES = [
  'ijWater',
  'riverWater',
  'canalWater',
] as const satisfies readonly WaterClass[];

// The layout's IJ carries no id of its own.
export const IJ_BODY_ID = 'ij';

// The bodies of water the site colours, by layout id: the IJ, each water area and each channel.
export function waterBodyIds(layout: Layout): readonly string[] {
  return [
    IJ_BODY_ID,
    ...layout.water.areas.map((area) => area.id),
    ...layout.water.channels.map((channel) => channel.id),
  ];
}

export function waterBodyKind(layout: Layout, id: string): WaterBodyKind | null {
  if (id === IJ_BODY_ID) return 'ij';
  if (layout.water.areas.some((area) => area.id === id)) return 'area';
  return layout.water.channels.find((channel) => channel.id === id)?.kind ?? null;
}

// The class each body of the file takes, by its layout id.
export function bodyClassesOf(file: WaterClassesFile): Readonly<Record<string, WaterClass>> {
  return Object.fromEntries(
    Object.entries(file.bodies).flatMap(([id, body]) => (body ? [[id, body.class]] : []))
  );
}

// The class most pixels voted for; a tie goes to the class listed first.
export function waterWinner(votes: WaterVotes): WaterClass {
  return WATER_CLASSES.reduce((best, cls) => (votes[cls] > votes[best] ? cls : best));
}

function coverageErrors(file: WaterClassesFile, ids: readonly string[]): string[] {
  const known = new Set(ids);
  return [
    ...ids.filter((id) => !file.bodies[id]).map((id) => `No class for water body ${id}`),
    ...Object.keys(file.bodies)
      .filter((id) => !known.has(id))
      .map((id) => `Class for ${id}, which is no water body of the layout`),
  ];
}

// A body lends its class only where its own pixels voted for it.
function votedBody(file: WaterClassesFile, id: string): WaterBody | null {
  const body = file.bodies[id];
  const voted = body && body.pixels >= file.method.minPixels;
  return voted && body.borrowedFrom === undefined && body.fallback === undefined ? body : null;
}

function borrowingErrors(id: string, body: WaterBody, file: WaterClassesFile): string[] {
  const from = body.borrowedFrom;
  if (from === undefined) return [];
  const lender = votedBody(file, from);
  return [
    ...(body.pixels >= file.method.minPixels
      ? [`${id} has ${body.pixels} voting pixels and cannot borrow`]
      : []),
    ...(body.fallback ? [`${id} borrows from ${from} and keeps the class of its kind`] : []),
    ...(lender ? [] : [`${id} borrows from ${from}, which did not vote`]),
    ...(lender && lender.class !== body.class
      ? [`${id} borrows ${body.class} from ${from}, which is ${lender.class}`]
      : []),
  ];
}

function voteErrors(id: string, body: WaterBody, file: WaterClassesFile): string[] {
  const { minPixels } = file.method;
  const votes = WATER_CLASSES.reduce((sum, cls) => sum + body.votes[cls], 0);
  if (votes !== body.pixels) return [`${id} has ${body.pixels} voting pixels and ${votes} votes`];
  if (body.borrowedFrom !== undefined) return borrowingErrors(id, body, file);
  if (body.fallback === 'kind') {
    return body.pixels >= minPixels
      ? [`${id} has ${body.pixels} voting pixels and cannot keep the class of its kind`]
      : [];
  }
  if (body.pixels < minPixels) {
    return [
      `${id} has ${body.pixels} voting pixels and neither borrows a class nor keeps the class of its kind`,
    ];
  }
  const won = waterWinner(body.votes);
  return won === body.class ? [] : [`${id} takes ${body.class} and its pixels voted ${won}`];
}

function bodyErrors(file: WaterClassesFile, layout: Layout): string[] {
  return Object.entries(file.bodies).flatMap(([id, body]) => {
    const kind = waterBodyKind(layout, id);
    if (!body || kind === null) return [];
    if (body.kind !== kind)
      return [`${id} is ${body.kind} water in the file and ${kind} water in the layout`];
    return voteErrors(id, body, file);
  });
}

// What the schema cannot see: the keys are exactly the layout's water bodies, each of its kind, and a
// body takes the class its pixels voted for unless too few voted, when it borrows the class of a
// body that voted or keeps the class of its kind.
export function checkWaterClasses(file: WaterClassesFile, layout: Layout): readonly string[] {
  return [...coverageErrors(file, waterBodyIds(layout)), ...bodyErrors(file, layout)];
}

let validate: ReturnType<ReturnType<typeof createAjvFactory>['compile']> | null = null;

export function parseWaterClasses(
  value: unknown,
  layout: Layout
): Result<WaterClassesFile, readonly string[]> {
  validate ??= createAjvFactory().compile(schema);
  if (!validate(value)) {
    return err(
      (validate.errors ?? []).map((e) => `${e.instancePath || '/'}: ${e.message ?? 'invalid'}`)
    );
  }
  const file = value as WaterClassesFile;
  const errors = checkWaterClasses(file, layout);
  return errors.length > 0 ? err(errors) : ok(file);
}
